import type { KnowledgeArticle } from "../types"

export const aiAgentBedrijfsdata: KnowledgeArticle = {
  id: "insight:ai-agent-bedrijfsdata",
  pageType: "knowledge-article",
  slug: { nl: "ai-agent-bedrijfsdata" },
  title: {
    nl: "Veilig Toegang geven aan een AI-Agent tot Bedrijfsdata",
    en: "Veilig Toegang geven aan een AI-Agent tot Bedrijfsdata",
  },
  description: {
    nl: "Hoe geef je een AI-agent veilige toegang tot bedrijfsdata? Rechten, grenzen, logging en ontwerpkeuzes die datalekken en misbruik voorkomen.",
    en: "How do you give an AI agent safe access to company data? Rights, boundaries, logging and design choices that prevent leaks and abuse.",
  },
  h1: {
    nl: "Veilig toegang geven aan een AI-agent tot bedrijfsdata",
    en: "Veilig toegang geven aan een AI-agent tot bedrijfsdata",
  },
  published: "2026-08-26",
  modified: "2026-08-26",
  author: "Camiel Schmeitz",
  sections: [
    {
      type: "paragraph",
      text: "De vraag die elk bestuur hoort te stellen voordat een agent wordt gebouwd: wat kan deze zien, en wat kan deze doen? Goede agent-beveiliging is geen aparte fase achteraf, maar een ontwerpkwestie vanaf dag één.",
    },
    {
      type: "heading",
      text: "Principe 1: minimale rechten",
    },
    {
      type: "paragraph",
      text: "Een agent krijgt per taak precies de bronnen die die taak vraagt — niet meer. Technisch betekent dat: geen gedeelde superuser-account, maar scoped credentials per integratie. De agent erft idealiter de rechten van de gebruiker namens wie hij handelt; kan hij meer dan die gebruiker, dan zit er een fout in het ontwerp.",
    },
    {
      type: "heading",
      text: "Principe 2: harde grenzen op acties",
    },
    {
      type: "paragraph",
      text: "Leg per agent vast welke acties zelfstandig mogen en welke een goedkeurstap vereisen. Leesbewerkingen kunnen vrijwel altijd automatisch; schrijf- en externe acties (mails versturen, orders plaatsen, records verwijderen) verdienen een drempel — zeker in de eerste periode. Grenzen horen in code, niet in een prompt: 'beloof me dat je dit niet doet' is geen beveiliging.",
    },
    {
      type: "heading",
      text: "Principe 3: log alles, audit regelmatig",
    },
    {
      type: "list",
      items: [
        "Elke opgevraagde bron en uitgevoerde actie wordt gelogd met context (wie, wanneer, waarom).",
        "Logs maken evaluatie mogelijk: welke antwoorden waren fout, welke acties onterecht?",
        "Periodieke review van logs is de enige manier om drift in gedrag op tijd te zien.",
      ],
    },
    {
      type: "heading",
      text: "Principe 4: let op datastromen richting modellen",
    },
    {
      type: "paragraph",
      text: "Weet welke data naar welke externe dienst gaat en onder welke voorwaarden. Privacygevoelige velden filter je vóór verzending, niet achteraf. Voor gevoelige omgevingen zijn hostingkeuzes en dataverwerkingsafspraken een selectiecriterium voor het model, geen bijzaak.",
    },
    {
      type: "heading",
      text: "Principe 5: verwacht misbruik",
    },
    {
      type: "paragraph",
      text: "Gebruikers proberen uit nieuwsgierigheid of kwaadwil grenzen te testen: prompt-injectie via documenten, verzoeken buiten scope, social engineering. Invoerfiltering, duidelijke weigerregels en het beperken van wat agent-output kan triggeren (bijv. geen directe code-executie) horen tot de basis.",
    },
    {
      type: "callout",
      title: "Samengevat",
      text: "Minimale rechten, harde actiegrenzen in code, volledige logging, bewuste datastromen en misbruikverwachting. Agents zijn krachtig juist doordat ze handelen — beheersbaar zijn ze alleen als handelen ontworpen wordt.",
    },
  ],
  relatedServiceIds: ["ai-agents", "ai-knowledge-systems"],
  relatedCaseIds: ["burgerkracht", "quantitative-trading"],
}
